import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { delay, tap } from 'rxjs/operators';
import { AliveState } from 'ZalamoLibrary/src/lib/core/ActiveState';
import { Properties } from '@ui/ivypinch_pro/interfaces';
import { UntypedFormControl } from '@angular/forms';
import { Required } from 'ZalamoLibrary/src/lib/core/decorators/Required';
import { ZalamoObject } from '@ui/orders/graphql';

@Component({
    selector: 'lib-single-photo-cropper',
    templateUrl: './single-photo-cropper.component.html',
    styleUrls: ['./single-photo-cropper.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SinglePhotoCropperComponent extends AliveState implements OnInit {
  pinchProps: Properties & { limitZoom?: number; scale?: number } = {
    disableZoomControl: 'never',
    zoomControlScale: 0.1,
    limitPan: true,
  };
  @Output() currentSet = new BehaviorSubject<{
    scale?: number;
    moveY?: number;
    moveX?: number;
  }>({});
  private _imageRatio = new BehaviorSubject(null);
  private _containerRatio = new BehaviorSubject(null);
  containerWidth$ = new BehaviorSubject(null);
  containerHeight$ = new BehaviorSubject(null);
  currentCrop = new UntypedFormControl();
  @ViewChild('zoom', { static: true }) pinchZoomComponent;
  @ViewChild('img', { static: true }) img: ElementRef<HTMLImageElement>;
  @Output() sharpCrop = new EventEmitter();
  @Input() @Required imagesContext: ZalamoObject;
  @Input() @Required contextId;
  @Input() currentCropValue: {
    x: number;
    y: number;
    w: number;
    h: number;
  } = {
    x: 0,
    y: 0,
    w: 1,
    h: 1,
  };

  minScale = 1;
  _pickedPhoto;
  @Input() set pickedPhoto(pickedPhoto) {
    this._pickedPhoto = pickedPhoto;
    this.imageRatio = pickedPhoto.photo.width / pickedPhoto.photo.height;
  }

  get pickedPhoto() {
    return this._pickedPhoto;
  }

  imageLoaded$ = new Subject();

  get imageRatio(): number {
    return this._imageRatio.value;
  }

  @Input() set imageRatio(value: number) {
    console.log(`

    IMAGE RATIO SET TO ${value}



    `);
    this._imageRatio.next(value);
  }

  get containerRatio(): number {
    return this._containerRatio.value;
  }

  @Input() set containerRatio(value: number) {
    console.log(`

    CONTAINER RATIO SET TO ${value}



    `);
    this._containerRatio.next(value);
  }

  @Output() updated = new EventEmitter();

  constructor(private ref: ElementRef<HTMLElement>) {
    super();
  }

  ngOnInit(): void {
    this.subscribeWhileAlive(
      combineLatest([
        this._containerRatio,
        this._imageRatio,
        this.imageLoaded$,
      ]).pipe(
        delay(35),
        tap(() => {
          const { width, height } = this.ref.nativeElement
            .querySelector('pinch-zoom')
            .getBoundingClientRect();

          if (this.currentCropValue) {
            this.pinchZoomComponent.setTransform(this.currentCropValue);
          }
          const imageSize = this.ref.nativeElement
            .querySelector('img')
            .getBoundingClientRect();
          console.log({
            imageSize,
          });
          const mineRatio = width / height;
          let containerWidth;
          let containerHeight;
          if (this._containerRatio.value > mineRatio) {
            containerWidth = width;
            containerHeight = width / this._containerRatio.value;
          } else {
            containerHeight = height;
            containerWidth = height * this._containerRatio.value;
          }
          this.containerWidth$.next(containerWidth);
          this.containerHeight$.next(containerHeight);

          if (this._imageRatio.value >= this._containerRatio.value) {
            const noScaleImageHeight =
              imageSize.height * (containerWidth / imageSize.width);
            const scale =
              containerHeight / this.currentCropValue.h / noScaleImageHeight;
            this.minScale = Math.min(
              containerHeight / noScaleImageHeight,
              scale,
            );
            this.pinchProps.minScale = this.minScale;
            this.pinchProps.minPanScale = this.minScale;
            this.pinchProps['limit-zoom'] = 5;
            this.pinchProps.limitZoom = 5;
            this.pinchProps.scale = this.minScale;
            this.pinchZoomComponent.minScale = this.minScale;
            this.pinchZoomComponent.applyOptionsDefault(this.pinchProps);
            this.pinchZoomComponent.properties = this.pinchProps;
            setTimeout(async () => {
              this.pinchZoomComponent.reset();

              await this.pinchZoomComponent.initPinchZoom();

              this.pinchZoomComponent.setTransform({
                x: this.currentCropValue.x * containerWidth * scale,
                y:
                  this.currentCropValue.y * noScaleImageHeight * scale -
                  ((containerHeight - noScaleImageHeight) / 2) * scale,
                scale,
              });
              this.pinchZoomComponent.pinchZoom.scale = scale;
              console.log(this.pinchZoomComponent);
              setTimeout(() => {
                this.events({
                  detail: {
                    scale: this.pinchZoomComponent.scale,
                    moveX: this.pinchZoomComponent.x,
                    moveY: this.pinchZoomComponent.y,
                  },
                });
              }, 1000);
            }, 10);
          } else {
            const noScaleImageWidth =
              imageSize.width * (containerHeight / imageSize.height);
            const scale =
              containerWidth / this.currentCropValue.w / noScaleImageWidth;
            this.minScale = Math.min(containerWidth / noScaleImageWidth, scale);
            this.pinchProps.minScale = this.minScale;
            this.pinchProps['limit-zoom'] = 15;
            this.pinchProps.limitZoom = 15;
            this.pinchProps.minPanScale = this.minScale;
            this.pinchZoomComponent.minScale = this.minScale;
            this.pinchZoomComponent.properties = this.pinchProps;
            this.pinchProps.scale = this.minScale;
            this.pinchZoomComponent.applyOptionsDefault(this.pinchProps);
            setTimeout(async () => {
              this.pinchZoomComponent.reset();

              await this.pinchZoomComponent.initPinchZoom();

              this.pinchZoomComponent.setTransform({
                y: this.currentCropValue.y * containerHeight * scale,
                x:
                  this.currentCropValue.x * noScaleImageWidth * scale -
                  ((containerWidth - noScaleImageWidth) / 2) * scale,
                scale,
              });
              this.pinchZoomComponent.pinchZoom.scale = scale;
              setTimeout(() => {
                this.events({
                  detail: {
                    scale,
                    moveX: this.currentCropValue.y * containerHeight * scale,
                    moveY:
                      this.currentCropValue.x * noScaleImageWidth * scale -
                      ((containerWidth - noScaleImageWidth) / 2) * scale,
                  },
                });
              }, 1000);
            }, 10);
          }
        }),
      ),
    );
  }

  events(events: {
    detail: { scale?: number; moveY?: number; moveX?: number };
  }) {
    const current = { ...this.currentSet.value };
    console.log(events);
    if (events.detail?.scale) {
      current.scale = events.detail?.scale;
    }
    if (typeof events.detail?.moveX === 'number') {
      current.moveX = events.detail?.moveX / this.containerWidth$.value;
    }
    if (typeof events.detail?.moveY === 'number') {
      current.moveY = events.detail?.moveY / this.containerHeight$.value;
    }
    console.log(current);
    this.currentSet.next(current);

    const container =
      this.pinchZoomComponent.elementRef.nativeElement.getBoundingClientRect();
    const image = this.img.nativeElement.getBoundingClientRect();
    const [y, w, h, x] = [
      (container.y - image.y) / image.height,
      container.width / image.width,
      container.height / image.height,
      (container.left - image.left) / image.width,
    ].map((v) => Math.abs(v * 100));
    this.currentCrop.patchValue({
      y,
      w,
      h,
      x,
    });
    console.log({
      x,
      y,
      w,
      h,
      imageWidth: image.width,
      imageHeight: image.height,
      containerWidth: container.width,
      containerHeight: container.height,
      originalImageWidth: this.img.nativeElement.naturalWidth,
      originalImageHeight: this.img.nativeElement.naturalHeight,
    });
    this.sharpCrop.emit({
      y,
      x,
      scale: 1 / (w / 100),
      crop: {
        x: -this.currentCrop.value.x / 100,
        y: -this.currentCrop.value.y / 100,
        w: this.currentCrop.value.w / 100,
        h: this.currentCrop.value.h / 100,
      },
    });
    // this.sharpCrop.emit(current);
  }
}
